import { AnimatePresence, motion } from 'motion/react'
import { useEffect } from 'react'
import TextHeadline from '../texts/TextHeadline'
import ButtonIcon from '../buttons/ButtonIcon'
import IconClose from '../icons/IconClose'
import clsx from 'clsx'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { TextHeadlineColorPreset } from '../texts/textConfig'
import Button from '../buttons/Button'
import IconCalendar from '../icons/IconCalendar'

export type StatModalContent = {
    icon?: string
    title: string
    description: string
    parentTitle?: string
    parentDescription?: string
    color?: TextHeadlineColorPreset;
}

type StatModalProps = {
    content: StatModalContent | null
    onClose: () => void
    /** Shown only under `parentDescription`; same behavior as parents landing appointment CTA. */
    onTerminVereinbaren?: () => void
}

const containerVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.15,
        },
    },
    exit: {
        transition: {
            staggerChildren: 0.05,
            staggerDirection: -1,
        },
    },
}

const cardVariants = {
    hidden: { scale: 0.85, opacity: 0, y: 20 },
    visible: {
        scale: 1,
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring' as const,
            stiffness: 380,
            damping: 22,
            mass: 0.8,
        },
    },
    exit: {
        scale: 0.9,
        opacity: 0,
        transition: { duration: 0.2 },
    },
}

const parentVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring' as const,
            stiffness: 320,
            damping: 26,
            mass: 0.7,
        },
    },
    exit: {
        opacity: 0,
        y: 16,
        transition: { duration: 0.18 },
    },
}

export default function StatModal({
    content,
    onClose,
    onTerminVereinbaren,
}: StatModalProps) {
    useEffect(() => {
        if (!content) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [content, onClose])

    const hasParentContent = Boolean(content?.parentTitle || content?.parentDescription)

    const isStele = useSteleBreakpoint();

    return (
        <AnimatePresence>
            {content && (
                <motion.div
                    className={clsx(
                        'flex flex-col justify-center items-center',
                        'gap-16',
                        'stele:gap-64'
                    )}
                    variants={containerVariants}
                    initial='hidden'
                    animate='visible'
                    exit='exit'
                >
                    {hasParentContent && !isStele && (
                        <motion.div
                            className={clsx(
                                'relative flex flex-col justify-center items-center bg-black/35',
                                'gap-2 p-4 rounded-3xl max-w-100',
                                'stele:gap-8 stele:p-16 stele:rounded-[96px] stele:max-w-400'
                            )}
                            variants={parentVariants}
                        >
                            {content.parentTitle && (
                                <p className='bg-white-gradient bg-clip-text drop-shadow-2-white-stroke font-grandstander font-bold text-[16px] text-transparent stele:text-[64px] italic uppercase'>  {content.parentTitle}</p>
                            )}
                            {content.parentDescription && (
                                <>
                                    <p
                                        className={clsx(
                                            'text-white-stroke-lightest font-nunito font-normal text-center leading-[135%] tracking-[0%]',
                                            'text-[12px]',
                                            'stele:text-[48px]'
                                        )}
                                    >
                                        {content.parentDescription}
                                    </p>
                                    {(onTerminVereinbaren && !isStele) ? (
                                        <div
                                            className={clsx(
                                                'flex justify-center w-full',
                                                'mt-3',
                                                'stele:mt-12'
                                            )}
                                        >
                                            <Button
                                                type='button'
                                                color='orange'
                                                icon={<IconCalendar width='100%' height='100%' />}
                                                onClick={onTerminVereinbaren}
                                            >
                                                Termin vereinbaren
                                            </Button>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </motion.div>
                    )}

                    <motion.div
                        key='stat-modal-card'
                        className={clsx(
                            'stele:absolute relative flex flex-col justify-center items-center drop-shadow-2-black-stroke bg-white-stroke-even-darker border-black-stroke',
                            'gap-4 px-6 py-8 border rounded-4xl max-w-100',
                            'stele:gap-16 stele:px-24 stele:py-32 stele:border-4 stele:rounded-[128px] stele:max-w-400 stele:bottom-163'
                        )}
                        variants={cardVariants}
                        role='dialog'
                        aria-modal='true'
                        aria-labelledby='stat-modal-title'
                    >
                        <div className='-top-8 stele:-top-30 absolute flex justify-center items-center w-full'>
                            {content.icon && (
                                <img
                                    src={content.icon}
                                    alt='decorative icon'
                                    className={clsx(
                                        'object-contain',
                                        'w-14 h-14 translate-x-15',
                                        'stele:w-48 stele:h-48 stele:translate-x-50'
                                    )}
                                />
                            )}
                            <TextHeadline
                                colorPreset={'blue'}
                                fontSize={isStele ? 96 : 28}
                            >
                                {content.title}
                            </TextHeadline>
                        </div>
                        <p
                            className={clsx(
                                'text-white-stroke-lightest font-nunito font-bold text-center tracking-[-2%]',
                                'text-[16px] leading-5.5',
                                'stele:text-[64px] stele:leading-22'
                            )}
                        >
                            {content.description}
                        </p>
                        <div
                            className={clsx(
                                'absolute',
                                '-bottom-6',
                                'stele:-bottom-24'
                            )}
                        >
                            <ButtonIcon icon={<IconClose width='100%' height='100%' />} variant='standalone' color='red' popupCloseSound activateOn='pointerup' onClick={onClose} />
                        </div>
                    </motion.div>


                </motion.div>
            )}
        </AnimatePresence>
    )
}
